// owner/screens/Report.js — one monthly report on screen (/finance/reports/:id). Decision A (Adil, 4 Nov 2026,
// tasks/owner-mobile-app-build.md §10 "8c batch 3"): the SAME renderer as the printed report — its masthead and
// MonthlyReportView in the document form — so one report has one look. It stays Arabic in English too: the report is
// an Arabic document whose insights the server writes in Arabic, and a second translated renderer would be a copy.
// The payload is gross only by design: no fee and no net reach it (be/services/ownerReport/reportBuilder.js).
// On the web «طباعة» opens the existing print page (outside the owner layout); the app has no print yet.

import React from 'react';
import { Link, useParams } from 'react-router-dom';
import useLanguage from '../../hooks/useLanguage';
import Icon from '../ui/Icon';
import { SkeletonBlocks, ErrorState, EmptyState } from '../ui/States';
import { IS_OWNER_APP } from '../app/appInfo';
import { useOwnerReport } from '../api/ownerReads';
import ReportMasthead from '../../components/revenue/MonthlyReports/ReportMasthead';
import MonthlyReportView from '../../components/revenue/MonthlyReports/MonthlyReportView';
import '../../styles/MonthlyReports.css';
// The masthead's own look lives with the print page; its other rules are scoped to `.report-document` /
// `.report-print-page`, which this screen does not use, so only the masthead is dressed here (no A4 sheet on a phone).
import '../../styles/OwnerReportPrint.css';

const Report = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const report = useOwnerReport(id);

  const back = <Link to="/finance" className="o-back" aria-label={t('ownerApp.report.back')}><Icon name="back" /></Link>;

  if (report.isLoading) return <>{back}<SkeletonBlocks blocks={3} /></>;
  if (report.isError) {
    if (report.error?.response?.status === 404) {
      return (
        <EmptyState icon="wallet" title={t('ownerApp.report.notAvailable')}
          action={<Link className="o-btn o-btn-primary" to="/finance">{t('ownerApp.report.back')}</Link>} />
      );
    }
    return <>{back}<ErrorState title={t('ownerApp.report.loadError')} onRetry={report.refetch} /></>;
  }

  const r = report.data;
  return (
    <>
      <div className="o-report-bar">
        {back}
        {!IS_OWNER_APP && (
          <Link className="o-btn o-btn-outline" to={`/finance/reports/${r._id}/print?auto=1`}>{t('ownerApp.report.print')}</Link>
        )}
      </div>
      <section className="o-section o-section-last o-report" lang="ar" dir="rtl">
        <ReportMasthead periodMonth={r.periodMonth} ownerName={r.ownerName} propertyCount={r.propertyCount} generatedAt={r.generatedAt} />
        <MonthlyReportView report={r} variant="print" />
      </section>
    </>
  );
};

export default Report;
